import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { saveAs } from "file-saver";
import { Tooltip } from "primereact/tooltip";
import { callReportSourceApi } from "utils/ApiClients";
import { useSelector, useDispatch } from "react-redux";
import { setPageData } from "store/actions";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import { t } from "i18next";

const CallReport = () => {
  const dispatch = useDispatch();
  const { first, rows, totalRecords } = useSelector((state) => state.calendar.pagination);
  const [pageState, setPageState] = useState({ page: 1, rows: 10 });
  const [loading, setLoading] = useState(true);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const dt = useRef(null);
  document.title = "ATS - Reports"

  const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info"));
  const intialcompanyId = userInfos.user_details.companies_data[0].company_id;
  const [companyId, setCompanyId] = useState(intialcompanyId);

  const handleCompanyChange = (company) => {
    if (company?.company_id) {
      const selectedId = company.company_id;
      setCompanyId(selectedId);

      const page = Math.floor(first / rows) + 1;
      // fetchCallReport(page, rows, selectedId);
    }
  };




  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    subtype: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    start_datetime: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    end_datetime: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    subject: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created_by_name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    completed_on: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const fetchCallReport = async (page, page_size , companyId ) => {
    try {
      // const response = await callReportSourceApi(`/call-report/?page=${page}&page_size=${page_size}`);
      setLoading(true);
      const response = await callReportSourceApi(`/call-report/?page=${page}&page_size=${page_size}&company_id=${companyId}`);
      const apiData = response?.data?.results || [];
      if (!apiData.length) {
        setSelectedCalls([]);
        setLoading(false); 
        return;
      }

      const transformedData = apiData.map((item, index) => {
        return {
          type: item.event_type || "",
          subtype: item.event_sub_type || "",
          start_datetime: item.start_date && item.start_time ? `${item.start_date} ${item.start_time}` : "",
          end_datetime: item.end_date && item.end_time ? `${item.end_date} ${item.end_time}` : "",
          subject: item.subject || "",
          created_by_name: item.created_by_name,
          completed_on: item.end_date || "",
        };
      });
      setSelectedCalls(transformedData);
      setLoading(false);
      dispatch(setPageData({
        first: (page - 1) * page_size,
        rows: page_size,
        totalRecords: response?.data?.count || 0,
      }));
    } catch (error) {
      setLoading(false);
      console.error("API Error:", {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      });
      setSelectedCalls([]);
    }
  };

  // useEffect(() => {
  //   const page = Math.floor(first / rows) + 1;
  //   fetchCallReport(page, rows);
  // }, [first, rows]);

  useEffect(() => {
    if (companyId) {
      const page = Math.floor(first / rows) + 1;
      fetchCallReport(page, rows, companyId);
    }
  }, [first, rows, companyId]);


  const onPage = (e) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    dispatch(setPageData({
      first: e.first,
      rows: e.rows,
      totalRecords,
    }));
    fetchCallReport(newPage, e.rows, companyId);
  };

  const onSelectionChange = (e) => {
    setSelectedRows(e.value);
  };

  const exportCSV = (selectionOnly) => {
    if (selectionOnly && selectedRows.length > 0) {
      dt.current.exportCSV({ selectionOnly: true });
    } else {
      dt.current.exportCSV({ selectionOnly: false });
    }
  };

  const exportPdf = () => {
  import("jspdf").then((jsPDF) => {
    import("jspdf-autotable").then(() => {
      const doc = new jsPDF.default("landscape");
      const dataToExport = selectedRows.length > 0 ? selectedRows : selectedCalls;

      if (dataToExport.length === 0) {
        toast.current.show({
          severity: "info",
          summary: "Info",
          detail: "No rows selected, exporting all data to PDF.",
          life: 3000,
        });
      }

      const exportColumns = [
        { title: "Type", dataKey: "type" },
        { title: "Subtype", dataKey: "subtype" },
        { title: "Start Date Time", dataKey: "start_datetime" },
        { title: "End Date Time", dataKey: "end_datetime" },
        { title: "Subject", dataKey: "subject" },
        { title: "Scheduled By", dataKey: "created_by_name" },
        { title: "Completed On", dataKey: "completed_on" },
      ];

      const formattedData = dataToExport.map(item => {
        const row = {};
        exportColumns.forEach(col => {
          row[col.dataKey] = item[col.dataKey] ?? '';
        });
        return row;
      });

      doc.autoTable({
        columns: exportColumns,
        body: formattedData.length > 0 ? formattedData : [{ type: "No data to export" }],
        startY: 20,
        styles: { fontSize: 8, cellPadding: 2 },
        theme: "grid",
        margin: { top: 10, left: 5, right: 5 },
        columnStyles: { 0: { cellWidth: 30 } },
      });

      doc.save("callreports.pdf");
    });
  });
};

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const dataToExport = selectedRows.length > 0 ? selectedRows : selectedCalls;
      const worksheet = xlsx.utils.json_to_sheet(
        dataToExport.length > 0 ? dataToExport : [{ type: "No data to export" }]
      );
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(excelBuffer, "selectedCalls");
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(
          data,
          fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
        );
      }
    });
  };

  const subjectTemplate = (rowData) => {
    const subject = rowData.subject || "";
    const words = subject ? subject.split(" ").slice(0, 2).join(" ") + "..." : "";
    return (
      <span data-pr-tooltip={subject} data-pr-position="top">
        {words}
      </span>
    );
  };

  const printFile = () => {
    const columns = [
      { title: "Type", field: "type" },
      { title: "Subtype", field: "subtype" },
      { title: "Start Date Time", field: "start_datetime" },
      { title: "End Date Time", field: "end_datetime" },
     
      { title: "Subject", field: "subject" },
      { title: "Scheduled By", field: "created_by_name" },
      { title: "Completed On", field: "completed_on" },
     
    ];

    const dataToPrint = selectedRows.length > 0 ? selectedRows : selectedCalls;
    const tableHeaders = columns
      .map(col => `<th style="border: 1px solid #ccc; padding: 8px;">${col.title}</th>`)
      .join('');
    const tableRows = dataToPrint.length > 0
      ? dataToPrint
          .map(row => {
            return `<tr>${columns
              .map(col => `<td style="border: 1px solid #ccc; padding: 8px;">${row[col.field] ?? ''}</td>`)
              .join('')}</tr>`;
          })
          .join('')
      : '<tr><td colspan="9" style="border: 1px solid #ccc; padding: 8px; text-align: center;">No data to print</td></tr>';

    const htmlContent = `
      <html>
      <head>
        <title>Print Call Report</title>
        <style>
          table {
            border-collapse: collapse;
            width: 100%;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 8px;
            text-align: left;
          }
          h2 {
            text-align: center;
          }
        </style>
      </head>
      <body>
        <h2>Call Report</h2>
        <table>
          <thead><tr>${tableHeaders}</tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '', 'height=600,width=900');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <React.Fragment>
      <div className="page-content allact-tabs">
        <Container fluid={true}>
          <div className="page-title-box actjobbread">
            <Row className="align-items-center actjobsum">
              <Col sm={6} md={6} lg={8}>
                <h1 className="page-title">Call Report</h1>
              </Col>
              <Col sm={6} md={6} lg={4}>
                <div className="allbtns d-flex gap-2 justify-content-end">
                  <Button
                    type="button"
                    icon="pi pi-file"
                    rounded
                    onClick={() => exportCSV(true)}
                    tooltip="Export to CSV"
                    tooltipOptions={{ position: "left" }}
                    className="csvbtn"
                  />
                  <Button
                    type="button"
                    icon="pi pi-file-excel"
                    severity="success"
                    rounded
                    onClick={exportExcel}
                    tooltip="Export to XLS"
                    tooltipOptions={{ position: "top" }}
                    className="xlsbtn"
                  />
                  <Button
                    type="button"
                    icon="pi pi-file-pdf"
                    severity="warning"
                    rounded
                    onClick={exportPdf}
                    tooltip="Export to PDF"
                    tooltipOptions={{ position: "bottom" }}
                    className="pdfbtn"
                  />
                  <Button
                    type="button"
                    icon="pi pi-print"
                    severity="warning"
                    rounded
                    tooltip="Print"
                    className="printbtn"
                    onClick={printFile}
                    tooltipOptions={{ position: "bottom" }}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col xxl={6}>
                <h1 className="page-title mb-4"> </h1>
              </Col>
              <Col xxl={6}>
                <div className="d-flex justify-content-end mt-2">
                  <label className="mt-2 me-2">Select Company</label>
                  <div className="mb-2 me-2" style={{ width: "170px" }}>
                    <CompanySelector onCompanyChange={handleCompanyChange} />
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <Tooltip target="span[data-pr-tooltip]" />
                <section className="job-datatable-section">
                    <div className="card1 mt-3 mb-4 actjobsumtable">             
                      {loading ? (
                        <OrgTableShimmer columns={6} rows={7} titleWidth="220px" />
                      ) : (
                    <DataTable
                      ref={dt}
                      value={selectedCalls}
                      rows={rows}
                      first={first}
                      totalRecords={totalRecords}
                      onPage={onPage}
                      filters={filters}
                      filterDisplay="row"
                      loading={loading}
                      globalFilterFields={[
                        "type",
                        "subtype",
                        "start_datetime",
                        "end_datetime",
                      
                        "subject",
                        "created_by_name",
                        "completed_on",
                        
                      ]}
                      selection={selectedRows}
                      onSelectionChange={onSelectionChange}
                      selectionMode="multiple"
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    >
                      <Column field="type" sortable header="Type" filter filterPlaceholder="Type" />
<Column field="subtype" sortable header="Subtype" filter filterPlaceholder="Subtype" />
<Column
  field="start_datetime"
  sortable
  header="Start Date Time"
  filter
  filterMatchMode={FilterMatchMode.DATE_IS}
  filterPlaceholder="Start Date Time"
/>
<Column
  field="end_datetime"
  sortable
  header="End Date Time"
  filter
  filterMatchMode={FilterMatchMode.DATE_IS}
  filterPlaceholder="End Date Time"
/>
<Column
  field="subject"
  sortable
  header="Subject"
  filter
  filterPlaceholder="Subject"
  body={subjectTemplate}
/>
<Column
  field="created_by_name"
  sortable
  header="Scheduled By"
  filter
  filterPlaceholder="Scheduled By"
/>
<Column
  field="completed_on"
  sortable
  header="Completed On"
  filter
  filterMatchMode={FilterMatchMode.DATE_IS}
  filterPlaceholder="Completed On"
/>
                    </DataTable>
                    )}
                  </div>
                </section>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CallReport;
